import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Avatar,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Book as BookIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import { teacherService, subjectService, timetableService, periodService } from '../../services/apiService';
import { formatDate } from '../../utils/dateFormat';
import { DataTable } from '../../components/Common';
import { getAvatarColor, getInitials } from '../../utils/avatar';

const TeacherView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const drawerWidth = 280;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teacherData, setTeacherData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [routine, setRoutine] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loadingRoutine, setLoadingRoutine] = useState(false);

  useEffect(() => {
    const fetchTeacherInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await teacherService.getById(id);
        
        if (response.success && response.data) {
          setTeacherData(response.data);
        } else if (response.data) {
          setTeacherData(response.data);
        } else {
          setError('Teacher not found');
        }
      } catch (err) {
        console.error('Error fetching teacher details:', err);
        setError(err.message || 'Failed to fetch teacher details');
      } finally {
        setLoading(false);
      }
    };

    const fetchSubjects = async () => {
      try {
        setLoadingSubjects(true);
        const response = await subjectService.getAll({ teacher_id: id });
        
        if (response.success && Array.isArray(response.data)) {
          setSubjects(response.data);
          // Extract unique classes from subjects
          const uniqueClasses = {};
          response.data.forEach(subject => {
            if (subject.class_id && !uniqueClasses[subject.class_id]) {
              uniqueClasses[subject.class_id] = {
                id: subject.class_id,
                name: subject.class_name,
                section: subject.class_section,
              };
            }
          });
          setClasses(Object.values(uniqueClasses));
        } else if (Array.isArray(response)) {
          setSubjects(response);
          const uniqueClasses = {};
          response.forEach(subject => {
            if (subject.class_id && !uniqueClasses[subject.class_id]) {
              uniqueClasses[subject.class_id] = {
                id: subject.class_id,
                name: subject.class_name,
                section: subject.class_section,
              };
            }
          });
          setClasses(Object.values(uniqueClasses));
        }
      } catch (err) {
        console.error('Error fetching subjects:', err);
      } finally {
        setLoadingSubjects(false);
      }
    };

    if (id) {
      fetchTeacherInfo();
      fetchSubjects();
    }
  }, [id]);

  useEffect(() => {
    const fetchRoutine = async () => {
      try {
        setLoadingRoutine(true);
        // Fetch periods first
        const periodsRes = await periodService.getAll();
        const allPeriods = periodsRes.data || periodsRes || [];
        setPeriods(allPeriods.filter(p => !/break/i.test(p.period_name || '')));
        
        // Fetch timetable for all classes this teacher teaches
        if (classes.length > 0) {
          const allRoutineEntries = [];
          for (const classItem of classes) {
            try {
              const routineRes = await timetableService.getAll({ class_id: classItem.id });
              const entries = routineRes.data || routineRes || [];
              // Filter entries where this teacher teaches the subject
              entries.forEach(entry => {
                if (entry.subject_id && entry.teacher_id === id) {
                  allRoutineEntries.push({
                    ...entry,
                    class_name: classItem.name,
                    class_section: classItem.section,
                  });
                }
              });
            } catch (err) {
              console.error(`Error fetching routine for class ${classItem.id}:`, err);
            }
          }
          setRoutine(allRoutineEntries);
        } else {
          setRoutine([]);
        }
      } catch (err) {
        console.error('Error fetching routine:', err);
      } finally {
        setLoadingRoutine(false);
      }
    };

    if (classes.length > 0) {
      fetchRoutine();
    }
  }, [classes, id]);

  const handleBack = () => {
    window.history.back();
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex' }}>
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${0}px` },
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
            backgroundColor: 'background.default',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex' }}>
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 2.5, md: 3 },
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${0}px` },
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
            backgroundColor: 'background.default',
          }}
        >
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <IconButton onClick={handleBack} color="primary">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6">Back to Teachers</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!teacherData) {
    return null;
  }

  // Prepare subjects data for table with SNo
  const subjectsWithSNo = subjects.map((subject, index) => ({
    ...subject,
    sno: index + 1,
  }));

  const subjectsColumns = [
    {
      header: 'SNo',
      field: 'sno',
      render: (value) => (
        <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {value}
        </Typography>
      ),
    },
    {
      header: 'Subject Name',
      field: 'name',
      render: (value) => (
        <Typography sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
      ),
    },
    {
      header: 'Class',
      field: 'class_name',
      render: (value, row) => {
        const className = value?.startsWith('Class ') ? value : `Class ${value || ''}`;
        return `${className}${row.class_section ? ` - ${row.class_section}` : ''}`.trim();
      },
    },
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <Header 
        onMenuClick={() => setSidebarOpen(true)} 
        showBackButton={true}
        onBack={handleBack}
        address={teacherData?.address || null}
      />
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        drawerWidth={drawerWidth}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${0}px` },
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: 'background.default',
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>

          {/* Basic Details Section - Improved Design */}
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2.5, sm: 3 },
              mb: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.12) 0%, rgba(25, 118, 210, 0.05) 100%)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
              {/* Avatar */}
              <Avatar
                src={teacherData.profile_image || null}
                alt={teacherData.full_name}
                sx={{
                  width: { xs: 72, sm: 80 },
                  height: { xs: 72, sm: 80 },
                  bgcolor: getAvatarColor(teacherData.full_name),
                  color: '#ffffff',
                  fontSize: { xs: '1.75rem', sm: '2rem' },
                  fontWeight: 700,
                  boxShadow: '0 4px 16px rgba(17, 82, 99, 0.4)',
                  border: '3px solid',
                  borderColor: 'background.paper',
                }}
              >
                {!teacherData.profile_image && getInitials(teacherData.full_name)}
              </Avatar>

              {/* Teacher Info */}
              <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 300 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {teacherData.full_name}
                  </Typography>
                  <Chip
                    label={teacherData.is_active ? 'Active' : 'Inactive'}
                    color={teacherData.is_active ? 'success' : 'default'}
                    size="medium"
                    sx={{ 
                      fontWeight: 600, 
                      height: 28,
                      fontSize: '0.875rem',
                    }}
                  />
                </Box>
                
                {/* Personal Details Grid */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                    gap: { xs: 1.5, sm: 2 },
                    mt: 2,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {teacherData.email || '—'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Phone
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {teacherData.phone || '—'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Date of Joining
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {formatDate(teacherData.date_of_joining) || '—'}
                    </Typography>
                  </Box>
                  
                  {teacherData.address && (
                    <Box sx={{ gridColumn: { xs: '1', sm: '1', md: '1 / -1' } }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Address
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {teacherData.address}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Tabs Section */}
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Tabs
              value={activeTab}
              onChange={(event, newValue) => setActiveTab(newValue)}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                  minHeight: 64,
                  fontSize: '0.9375rem',
                },
              }}
            >
              <Tab
                icon={<BookIcon />}
                iconPosition="start"
                label="Subjects & Classes"
                sx={{ minWidth: { xs: 'auto', sm: 200 } }}
              />
              <Tab
                icon={<ScheduleIcon />}
                iconPosition="start"
                label="Routine"
                sx={{ minWidth: { xs: 'auto', sm: 200 } }}
              />
            </Tabs>

            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              {/* Subjects & Classes Tab */}
              {activeTab === 0 && (
                <Box>
                  {loadingSubjects ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : subjects.length > 0 ? (
                    <DataTable
                      data={subjectsWithSNo}
                      columns={subjectsColumns}
                      emptyMessage="No subjects assigned"
                      showActions={false}
                    />
                  ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      No subjects assigned to this teacher.
                    </Alert>
                  )}
                </Box>
              )}

              {/* Routine Tab */}
              {activeTab === 1 && (
                <Box>
                  {loadingRoutine ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : routine.length > 0 ? (
                    <Box>
                      {/* Group routine by day */}
                      {[1, 2, 3, 4, 5, 6].map((day) => {
                        const dayRoutine = routine.filter(r => r.day_of_week === day);
                        if (dayRoutine.length === 0) return null;
                        
                        const dayNames = {
                          1: 'Monday',
                          2: 'Tuesday',
                          3: 'Wednesday',
                          4: 'Thursday',
                          5: 'Friday',
                          6: 'Saturday',
                        };
                        
                        // Sort by period start time
                        const sortedRoutine = dayRoutine.sort((a, b) => {
                          const periodA = periods.find(p => p.id === a.period_id);
                          const periodB = periods.find(p => p.id === b.period_id);
                          const timeA = periodA?.start_time || '';
                          const timeB = periodB?.start_time || '';
                          return timeA.localeCompare(timeB);
                        });
                        
                        return (
                          <Box key={day} sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}>
                              {dayNames[day]}
                            </Typography>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                                gap: 1.5,
                              }}
                            >
                              {sortedRoutine.map((entry, index) => {
                                const period = periods.find(p => p.id === entry.period_id);
                                const className = entry.class_name?.startsWith('Class ') 
                                  ? entry.class_name 
                                  : `Class ${entry.class_name || ''}`;
                                const classLabel = `${className}${entry.class_section ? ` - ${entry.class_section}` : ''}`.trim();
                                
                                return (
                                  <Paper
                                    key={`${day}-${entry.period_id}-${index}`}
                                    variant="outlined"
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 2,
                                      borderColor: 'divider',
                                      '&:hover': {
                                        boxShadow: 2,
                                        borderColor: 'primary.main',
                                      },
                                    }}
                                  >
                                    <Stack spacing={0.5}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        {period?.period_name || 'Period'} {period?.start_time && period?.end_time 
                                          ? `(${period.start_time} - ${period.end_time})` 
                                          : ''}
                                      </Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                        {entry.subject_name || '—'}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {classLabel}
                                      </Typography>
                                    </Stack>
                                  </Paper>
                                );
                              })}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      No routine assigned for this teacher.
                    </Alert>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default TeacherView;

